import type { CSSProperties } from 'react'
import * as stylex from '@stylexjs/stylex'

const styles = stylex.create({
  default: { height: '20px', width: '20px' },
})
export type IconName =
  | 'activity'
  | 'arrow-left'
  | 'arrow-right'
  | 'banknote'
  | 'calendar'
  | 'check'
  | 'chevron'
  | 'close'
  | 'clothing'
  | 'dashboard'
  | 'entertainment'
  | 'food'
  | 'gift'
  | 'groups'
  | 'loans'
  | 'menu'
  | 'people'
  | 'plus'
  | 'receipt'
  | 'rent'
  | 'search'
  | 'settle'
  | 'shopping'
  | 'transport'
  | 'travel'
  | 'sync'
  | 'utilities'
  | 'wallet'

type IconProps = {
  name: IconName
  className?: string
  style?: CSSProperties
}

export default function Icon({ name, className = 'h-5 w-5', style }: IconProps) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  }

  const iconProps = stylex.props(styles.default)

  return (
    <svg aria-hidden="true" {...iconProps} className={[iconProps.className, className].filter(Boolean).join(' ') || undefined} style={{ ...iconProps.style, ...style }} viewBox="0 0 24 24" {...common}>
      {name === 'dashboard' && <><path d="m3 10 9-7 9 7v10H3z" /><path d="M9 20v-7h6v7" /></>}
      {name === 'groups' && <><circle cx="8" cy="8" r="3" /><circle cx="17" cy="8" r="3" /><path d="M2.5 20v-1.5a5.5 5.5 0 0 1 11 0V20M12.5 15.5A5.5 5.5 0 0 1 21.5 20" /></>}
      {name === 'people' && <><circle cx="10" cy="8" r="3" /><path d="M4 20v-2a6 6 0 0 1 12 0v2M17 6.5a3 3 0 0 1 0 5.8M18 15a5 5 0 0 1 3 4.6" /></>}
      {name === 'activity' && <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>}
      {name === 'wallet' && <><path d="M4 7h14a2 2 0 0 1 2 2v10H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11" /><path d="M15 11h7v5h-7a2.5 2.5 0 0 1 0-5Z" /></>}
      {name === 'loans' && <><path d="M4 7.5A2.5 2.5 0 0 1 6.5 5h11A2.5 2.5 0 0 1 20 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 16.5z" /><path d="M4 9h16M8 14h3" /><circle cx="16" cy="14" r="1.5" /></>}
      {name === 'receipt' && <><path d="M7 3h10v18l-2.5-1.7L12 21l-2.5-1.7L7 21z" /><path d="M10 8h4M10 12h4M10 16h2" /></>}
      {name === 'rent' && <><path d="m3 10 9-7 9 7" /><path d="M5 9v11h14V9M9 20v-6h6v6" /></>}
      {name === 'search' && <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></>}
      {name === 'food' && <><path d="M6 3v8M3 3v5a3 3 0 0 0 6 0V3M6 11v10M16 3v18M16 3c3 2 3 7 0 9" /></>}
      {name === 'clothing' && <path d="m8 4 4 2 4-2 4 4-3 3-2-2v11H9V9l-2 2-3-3z" />}
      {name === 'travel' && <><path d="M4 7h16v13H4z" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M8 11h8M8 15h8" /></>}
      {name === 'transport' && <><path d="m5 16 1.5-6h11L19 16v4H5z" /><path d="M7 10 8 6h8l1 4M7 20v1M17 20v1M7 16h.01M17 16h.01" /></>}
      {name === 'gift' && <><path d="M3 10h18v11H3zM12 10v11M2 7h20v3H2z" /><path d="M12 7H8.5a2.5 2.5 0 1 1 2.5-2.5V7ZM12 7h3.5A2.5 2.5 0 1 0 13 4.5V7Z" /></>}
      {name === 'entertainment' && <><path d="m4 8 2-4 4 2 2-2 2 2 4-2 2 4v11H4z" /><path d="M8 12h.01M16 12h.01M9 16h6" /></>}
      {name === 'shopping' && <><path d="M4 8h16l-1 12H5z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></>}
      {name === 'utilities' && <path d="m13 2-9 12h7l-1 8 9-12h-7z" />}
      {name === 'settle' && <><path d="M4 8h13M13 4l4 4-4 4M20 16H7M11 12l-4 4 4 4" /></>}
      {name === 'sync' && <><path d="M20 7v5h-5" /><path d="M4 17v-5h5" /><path d="M6.2 9A6.5 6.5 0 0 1 20 12M4 12a6.5 6.5 0 0 0 13.8 3" /></>}
      {name === 'banknote' && <><rect x="3" y="6" width="18" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M7 9h.01M17 15h.01" /></>}
      {name === 'calendar' && <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M7 3v4M17 3v4M3 10h18" /></>}
      {name === 'plus' && <path d="M12 5v14M5 12h14" />}
      {name === 'chevron' && <path d="m8 10 4 4 4-4" />}
      {name === 'arrow-left' && <path d="m14 6-6 6 6 6M8 12h10" />}
      {name === 'arrow-right' && <path d="m10 6 6 6-6 6M16 12H6" />}
      {name === 'check' && <path d="m5 12 4 4L19 6" />}
      {name === 'close' && <path d="m6 6 12 12M18 6 6 18" />}
      {name === 'menu' && <path d="M4 7h16M4 12h16M4 17h16" />}
    </svg>
  )
}
